import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Play, Eye, EyeOff, Copy, Check } from 'lucide-react';
function SettingsCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-[#1c1c22] rounded-[16px] p-6 shadow-xl shadow-black/20" data-id="element-1524">
      <h2 className="text-xs font-semibold text-[#8a8680] uppercase tracking-wider mb-4" data-id="element-1525">{title}</h2>
      {children}
    </motion.div>;
}
function SettingsRow({
  label,
  children,
  hint
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return <div className="py-3" data-id="element-1526">
      <div className="flex items-center justify-between" data-id="element-1527">
        <span className="text-sm text-[#8a8680]" data-id="element-1528">{label}</span>
        <div data-id="element-1529">{children}</div>
      </div>
      {hint && <p className="text-[12px] text-[#8a8680]/40 mt-1" data-id="element-1530">{hint}</p>}
    </div>;
}
function Divider() {
  return <div className="h-px bg-[#8a8680]/10" data-id="element-1531" />;
}
function Toggle({
  value,
  onChange
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button onClick={() => onChange(!value)} className={`w-10 h-6 rounded-full transition-colors relative ${value ? 'bg-[#4a9a9a]' : 'bg-[#252530]'}`} data-id="element-1532">
      <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${value ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-1533" />
    </button>;
}
function Select({
  value,
  options
}: {
  value: string;
  options: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(value);
  return <div className="relative" data-id="element-1534">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded bg-[#252530] text-[#e8e4df] text-sm min-w-[160px] justify-between" data-id="element-1535">
        {selected}<ChevronDown size={14} className="text-[#8a8680]" data-id="element-1536" />
      </button>
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-1537" />
          <div className="absolute right-0 mt-2 rounded-lg shadow-xl overflow-hidden z-20 min-w-[160px] bg-[#252530]" data-id="element-1538">
            {options.map(opt => <button key={opt} onClick={() => {
          setSelected(opt);
          setIsOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#4a9a9a]/10 transition-colors" style={{
          color: selected === opt ? '#4a9a9a' : '#e8e4df'
        }} data-id="element-1539">{opt}</button>)}
          </div>
        </>}
    </div>;
}
function EditableUrl({
  value: initial
}: {
  value: string;
}) {
  const [value, setValue] = useState(initial);
  const [editing, setEditing] = useState(false);
  if (editing) return <input type="url" value={value} onChange={e => setValue(e.target.value)} onBlur={() => setEditing(false)} onKeyDown={e => e.key === 'Enter' && setEditing(false)} className="bg-[#252530] text-[#e8e4df] text-sm px-3 py-1.5 rounded-md outline-none w-[260px] font-mono" autoFocus data-id="element-1540" />;
  return <button onClick={() => setEditing(true)} className="text-sm text-[#e8e4df] hover:bg-[#252530] px-3 py-1.5 rounded-md transition-colors font-mono max-w-[260px] truncate" data-id="element-1541">{value}</button>;
}
function SecretKey({
  value
}: {
  value: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const masked = value.length > 8 ? `${value.slice(0, 4)}${'\u2022'.repeat(Math.max(value.length - 8, 4))}${value.slice(-4)}` : '\u2022'.repeat(8);
  const display = revealed ? value : masked;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return <div className="flex items-center gap-2" data-id="element-1542">
      <span className="text-sm text-[#e8e4df] font-mono bg-[#252530] px-3 py-1.5 rounded-md" data-id="element-1543">{display}</span>
      <button onClick={() => setRevealed(!revealed)} className="p-1.5 rounded-md hover:bg-[#252530] text-[#8a8680] hover:text-[#e8e4df] transition-colors" title={revealed ? 'Hide' : 'Reveal'} data-id="element-1544">
        {revealed ? <EyeOff size={14} data-id="element-1545" /> : <Eye size={14} data-id="element-1546" />}
      </button>
      <button onClick={copy} className="p-1.5 rounded-md hover:bg-[#252530] text-[#8a8680] hover:text-[#e8e4df] transition-colors" title="Copy" data-id="element-1547">
        {copied ? <Check size={14} className="text-[#4a9a9a]" data-id="element-1548" /> : <Copy size={14} data-id="element-1549" />}
      </button>
    </div>;
}
export function SettingsView() {
  const [alerts, setAlerts] = useState(true);
  const [sound, setSound] = useState(false);
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1550">
      <div className="max-w-[640px] mx-auto w-full flex flex-col gap-6" data-id="element-1551">
        <header data-id="element-1552">
          <h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-1553">Settings</h1>
          <p className="text-[#8a8680] text-[14px]" data-id="element-1554">Preferences and configuration.</p>
        </header>

        <SettingsCard title="Voice & Model" data-id="element-1555">
          <SettingsRow label="Default model" data-id="element-1556"><Select value="GPT-4o-mini" options={['GPT-4o-mini', 'GPT-4o', 'Gemini Flash', 'Gemini Pro']} data-id="element-1557" /></SettingsRow>
          <Divider data-id="element-1558" />
          <SettingsRow label="Voice" data-id="element-1559">
            <div className="flex items-center gap-2" data-id="element-1560">
              <Select value="Marin" options={['Marin', 'Ash', 'Ballad', 'Coral', 'Sage', 'Verse']} data-id="element-1561" />
              <button className="w-8 h-8 rounded-lg bg-[#252530] hover:bg-[#2a2a35] flex items-center justify-center transition-colors" data-id="element-1562"><Play size={14} className="text-[#8a8680]" data-id="element-1563" /></button>
            </div>
          </SettingsRow>
        </SettingsCard>

        <SettingsCard title="Notifications" data-id="element-1564">
          <SettingsRow label="Background agent alerts" data-id="element-1565"><Toggle value={alerts} onChange={setAlerts} data-id="element-1566" /></SettingsRow>
          <Divider data-id="element-1567" />
          <SettingsRow label="Sound" data-id="element-1568"><Toggle value={sound} onChange={setSound} data-id="element-1569" /></SettingsRow>
        </SettingsCard>

        <SettingsCard title="Data" data-id="element-1570">
          <SettingsRow label="Gmail sync" data-id="element-1571"><span className="text-[13px] text-[#8a8680]/60" data-id="element-1572">Every 5 min</span></SettingsRow>
          <Divider data-id="element-1573" />
          <SettingsRow label="Jira sync" data-id="element-1574"><span className="text-[13px] text-[#8a8680]/60" data-id="element-1575">Every 5 min</span></SettingsRow>
          <Divider data-id="element-1576" />
          <SettingsRow label="Calendar sync" data-id="element-1577"><span className="text-[13px] text-[#8a8680]/60" data-id="element-1578">Every 5 min</span></SettingsRow>
          <Divider data-id="element-1579" />
          <SettingsRow label="Last synced" data-id="element-1580"><span className="text-[13px] text-[#8a8680]/60" data-id="element-1581">2 min ago</span></SettingsRow>
        </SettingsCard>

        <SettingsCard title="Account" data-id="element-1582">
          <SettingsRow label="Name" data-id="element-1583"><span className="text-sm text-[#e8e4df]" data-id="element-1584">Matt Bourne</span></SettingsRow>
          <Divider data-id="element-1585" />
          <SettingsRow label="Email" data-id="element-1586"><span className="text-sm text-[#e8e4df]" data-id="element-1587">matt@contextloops.com</span></SettingsRow>
          <Divider data-id="element-1588" />
          <div className="py-3" data-id="element-1589">
            <p className="text-[12px] text-[#8a8680]/50 mb-3 leading-[1.5]" data-id="element-1590">You provide the backend. Zenbot is the frontend layer on top of your self-hosted Convex instance.</p>
            <div className="space-y-3" data-id="element-1591">
              <div className="flex items-center justify-between" data-id="element-1592">
                <span className="text-sm text-[#8a8680]" data-id="element-1593">Self-host URL</span>
                <EditableUrl value="https://convex.matt.dev" data-id="element-1594" />
              </div>
              <div className="flex items-center justify-between" data-id="element-1595">
                <span className="text-sm text-[#8a8680]" data-id="element-1596">Self-host key</span>
                <SecretKey value="sk_live_4f2a8b3c9d1e7f5a6b4c2d8e9f1a3b5c7d9e1f2a4b6c8d0e" data-id="element-1597" />
              </div>
            </div>
          </div>
          <Divider data-id="element-1598" />
          <div className="pt-4" data-id="element-1599">
            <button className="text-[13px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-1600">Sign out</button>
          </div>
        </SettingsCard>
      </div>
    </div>;
}